import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  Alert,
  Chip,
  Menu,
  MenuItem,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import AppsIcon from "@mui/icons-material/Apps";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";

import TableTopControls from "common/TableTopControls";

import DatosBasicosApi from "api/datosbasicosApi";
import EditParametroModal from "./editParametroModal";

const ParametrosTable = () => {
  const [parametros, setParametros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedParametro, setSelectedParametro] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const fetchParametros = async () => {
    try {
      const response = await DatosBasicosApi.fetchConfigParametros();
      setParametros(response.data);
      setError(null);
    } catch (error) {
      console.error("Error al obtener los parámetros generales:", error);
      setError(obtenerMensajeError(error, "Error al cargar los Parámetros Generales."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParametros();
  }, []);

  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleEdit = (parametro) => {
    setSelectedParametro(parametro);
    setEditModalOpen(true);
    handleMenuClose();
  };

  const filteredParametros = parametros.filter((parametro) => {
    const termino = searchTerm.toLowerCase();
    return (
      parametro.nombre.toLowerCase().includes(termino) ||
      parametro.descripcion?.toLowerCase().includes(termino) ||
      parametro.valor?.toLowerCase().includes(termino)
    );
  });

  const paginatedParametros = filteredParametros.slice(page * pageSize, (page + 1) * pageSize);

  const columns = [
    {
      field: "actions",
      renderHeader: () => <AppsIcon />,
      width: 50,
      sortable: false,
      align: "left",
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleMenuClick(e, params.row.id)}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
    {
      field: "nombre",
      headerName: "Nombre",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.nombre}
        </SuiTypography>
      ),
    },
    {
      field: "descripcion",
      headerName: "Descripción",
      flex: 4,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="regular">
          {params.row.descripcion}
        </SuiTypography>
      ),
    },
    {
      field: "valor",
      headerName: "Valor",
      flex: 3,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium" sx={{ wordBreak: "break-all" }}>
          {params.row.valor}
        </SuiTypography>
      ),
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 110,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.estado ? "Activo" : "Inactivo"}
          color={params.row.estado ? "success" : "default"}
        />
      ),
    },
  ];

  return (
    <SuiBox>
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          boxShadow: "none",
          backgroundColor: "transparent",
        }}
      >
        <SuiBox
          justifyContent="space-between"
          alignItems="center"
          p={1}
          sx={{
            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
              `${borderWidth[1]} solid ${borderColor}`,
          }}
        >
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiBox display="flex" flexDirection="column">
                <strong>Parámetros Generales</strong>
              </SuiBox>
            </Grid>
          </Grid>
          <SuiBox display="flex" alignItems="center" gap={2}>
            <Tooltip title="Buscar por Nombre, Descripción y Valor" arrow>
              <TextField
                size="small"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Tooltip>
            <TableTopControls
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalRows={filteredParametros.length}
            />
            <SuiButton
              variant="gradient"
              buttonColor="dark"
              size="small"
              onClick={() => {
                setSelectedParametro(null);
                setEditModalOpen(true);
              }}
            >
              Nuevo Parámetro
            </SuiButton>
          </SuiBox>
        </SuiBox>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <DataGrid
          rows={paginatedParametros}
          columns={columns}
          disableSelectionOnClick
          loading={loading}
          getRowHeight={() => "auto"}
          sx={{
            border: 0,
            backgroundColor: "white",
            borderRadius: 2,
            p: 2,
            [`& .${gridClasses.cell}`]: {
              borderBottom: "none",
              padding: "2px 2px",
            },
            [`& .${gridClasses.row}`]: {
              backgroundColor: ({ palette: { white } }) => white.main,
              "&:hover": {
                backgroundColor: ({ palette: { grey } }) => grey[100],
              },
              minHeight: "48px !important",
            },
            [`& .${gridClasses.columnHeaders}`]: {
              backgroundColor: ({ palette: { grey } }) => grey[50],
              borderRadius: 1,
              minHeight: "48px !important",
            },
            [`& .${gridClasses.footerContainer}`]: {
              display: "none",
            },
          }}
          hideFooter
        />
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleEdit(parametros.find((p) => p.id === selectedId))}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Editar
        </MenuItem>
      </Menu>

      <EditParametroModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        parametro={selectedParametro}
        onSuccess={() => {
          fetchParametros();
          setEditModalOpen(false);
        }}
      />
    </SuiBox>
  );
};

export default ParametrosTable;
